import { ReactElement } from 'react'
import { Box, Button, Card, Chip, Typography, Tooltip } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import { CorrectIcon, IncorrectIcon } from 'src/v3/core/icons'
import { useNavigate } from 'react-router-dom'
import { AssignmentCourseDetail } from '../types'

type AssignmentCourseCardProps = {
  course: AssignmentCourseDetail
}

function getCourseImageUrl(imageName?: string): string {
  return `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${imageName}`
}

export default function AssignmentCourseCard({
  course,
}: AssignmentCourseCardProps): ReactElement {
  let navigate = useNavigate()
  let isCompleted = course.status === 'Complete'

  let handleGoToCourse = () => {
    navigate(`/self-paced/course/${course.id}`)
  }

  return (
    <Card
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
      role="article"
      aria-labelledby={`course-name-${course.id}`}
    >
      {/* Completion Indicator */}
      <Tooltip title={isCompleted ? 'Completed' : 'Incomplete'}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: isCompleted ? 'success.main' : 'action.disabledBackground',
            flexShrink: 0,
          }}
          role="status"
          aria-label={isCompleted ? 'Completed' : 'Incomplete'}
        >
          {isCompleted ? (
            <CorrectIcon
              sx={{ fontSize: 24, color: 'white' }}
              aria-hidden="true"
            />
          ) : (
            <IncorrectIcon
              sx={{ fontSize: 24, color: 'text.disabled' }}
              aria-hidden="true"
            />
          )}
        </Box>
      </Tooltip>
      {/* Course Thumbnail */}
      <Box
        component="img"
        src={getCourseImageUrl(course.imageName)}
        alt={course.name}
        sx={{
          width: 80,
          height: 80,
          objectFit: 'cover',
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
      {/* Course Details */}
      <Box className="flex-1 min-w-0">
        <Typography
          id={`course-name-${course.id}`}
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {course.name}
        </Typography>

        <Box className="flex flex-wrap items-center gap-2">
          {/* CE Hours */}
          <Chip
            size="small"
            label={`${course.ceHours} CE hour${course.ceHours === 1 ? '' : 's'}`}
            variant="outlined"
            aria-label={`${course.ceHours} continuing education hours`}
          />

          {/* Section */}
          {course.sectionName && (
            <Chip
              color="primary"
              size="small"
              label={course.sectionName}
              variant="outlined"
              aria-label={`Section: ${course.sectionName}`}
            />
          )}
        </Box>
      </Box>
      {/* Go to Course Button */}
      <Button
        color="primary"
        variant="contained"
        endIcon={<ChevronRight />}
        onClick={handleGoToCourse}
        sx={{
          flexShrink: 0,
        }}
        aria-label={`Go to ${course.name} course`}
      >
        Go to Course
      </Button>
    </Card>
  )
}
